import { Field, Form, Formik } from "formik";
import React, { useContext, useRef } from "react";
import { Container, FormControl, useTheme, Paper } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import DataLayerService from "utility/dataLayerService";
import { useComponentName } from "hooks";

// Import SVG Icons.
import { ReactComponent as SeniorIcon } from "components/Forms/form-icons/senior_15963a0d3a.svg";
import { ReactComponent as MilitaryIcon } from "components/Forms/form-icons/military_15956ee5f5.svg";
import { ReactComponent as NeitherIcon } from "components/Forms/form-icons/neither_15957a1137.svg";

import styled from "@emotion/styled";
import TermAndCondition from "components/atoms/TermAndCondition";
import { LeafButton } from "components/atoms/LeafButtons";
import LeafBox from "components/atoms/LeafBox";
import ProgressBar from "components/atoms/ProgressBar";
import ProgressBarWithPercentage from 'components/atoms/ProgressBarWithPercentage';
import { useVersion } from "hooks";
import { scrollToTop } from '../../../../utility/functions';
import { FormContext } from "App";

import { WrapFormControlLabel } from "./WrapFormControlLabel"; // Adjust path as needed

const CustomerGroupStep = (props) => {
  const location = props.dataSet?.location ? props.dataSet.location : "multi-step-modal-form";
  const form = props.dataSet?.form ? props.dataSet.form : "modal";
  const { setactiveLHWSStepIndex, formData, setFormData } = useContext(FormContext);
  const product = 'lhws';
  const userJourneyTracking = props.dataSet?.tracking || '';
  const componentName = props.component || 'ZipChecker';
  const formName = useComponentName(componentName);
  const subformName = useComponentName(props.modalComponent);
  const v2 = useVersion();
  const formRef = useRef(null);
  const secondEmailStep = props.dataSet?.secondemailstep ?? '';
  const splitNameEmailStep = props.dataSet?.splitnameemailstep ?? '';

  // Initialize groupList from formData if it exists, otherwise empty array.
  // This is important for "neither" logic and re-rendering with initial values.
  let groupList = Array.isArray(formData.groupLevel) ? [...formData.groupLevel] : [];
  if (formData.groupLevel === 'neither') {
    groupList = ['neither']; // If 'neither' was previously selected, initialize with it.
  }

  const handleImageClick = (value, setFieldValue) => {
    // Determine if 'neither' is selected
    const isNeitherSelected = groupList.includes('neither');
    // Determine if the clicked value is 'neither'
    const isClickedNeither = value === 'neither';

    // If 'neither' is clicked:
    if (isClickedNeither) {
      // If 'neither' was already selected, deselect it (toggle off)
      if (isNeitherSelected) {
        groupList = [];
      } else {
        // If 'neither' was not selected, select it exclusively
        groupList = ['neither'];
      }
    } else { // If a non-'neither' option is clicked:
      // Deselect 'neither' if it was previously selected
      if (isNeitherSelected) {
        groupList = [];
      }

      const groupListIndex = groupList.indexOf(value);
      if (groupListIndex > -1) {
        groupList.splice(groupListIndex, 1); // Remove if already present
      } else {
        groupList.push(value); // Add if not present
      }
    }

    setFieldValue("groupLevel", groupList.length > 0 ? groupList : ""); // Set to array or empty string if nothing selected

    // Passing lf.form.interaction event to DataLayer.
    DataLayerService.formInteractionEvent(form, location);
  };


  const handleOnBlur = (event) => {
    // Passing lf.form.interaction event to DataLayer.
    DataLayerService.formInteractionEvent(form, location);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const StyledFormContainer = styled(Paper)(() => ({
    background: "#ffffff",
    borderRadius: 0,
    maxWidth: isMobile ? "100%" : 520,
    width: "100%",
    height: isMobile ? "auto" : 720,
  }));


  return (
    <Formik
      initialValues={{
        groupLevel: formData.groupLevel || [], // Initialize as empty array for multi-select, or existing value
      }}
      onSubmit={(values) => {
        const data = { ...formData, ...values };
        setFormData(data);
        if (splitNameEmailStep === 'enable') {
          setactiveLHWSStepIndex('name');
        }
        else {
          if (secondEmailStep === 'enable') {
            setactiveLHWSStepIndex('project');
          }
          else {
            setactiveLHWSStepIndex('details');
          }
        }

        // Scroll to the Top.
        scrollToTop(formRef);

        if (userJourneyTracking) {
          let stepNumber = 2;
          if ((componentName === 'ZipChecker') || (componentName === 'ModalForm')) {
            stepNumber = 3;
          }

          let requiredFields = 1;
          if (secondEmailStep === 'enable') {
            requiredFields = 4;
            stepNumber += 1;
          }

          // Passing lf.form.status event to DataLayer.
          DataLayerService.formStatusEvent(formName, subformName, product, stepNumber, requiredFields, 'notcompleted');
        }

        // Passing lf.form.start event to DataLayer.
        DataLayerService.formStartEvent(form, location, data);
      }}
    >
      {({ handleSubmit, setFieldValue, values, isSubmitting, errors }) => (
        <Container maxWidth={isMobile ? "xs" : "sm"} disableGutters={true}>
          <StyledFormContainer>
            <Form
              className="flex flex-col justify-center items-start"
              onSubmit={handleSubmit}
              ref={formRef}
              onBlur={
                (event) => { handleOnBlur(event) }
              }
            >
              {isSubmitting && Object.keys(errors).length > 0 && scrollToTop(formRef)}
              <div className="step2">
                <h3 className="form-step-title text-2xl fw-700">
                  Does anyone in your household qualify for an extra 10% senior
                  or military discount?
                </h3>
              </div>

              <FormControl component="fieldset">
                <legend id="group-level-legend" className="font-medium text-green-600">
                  Which of these applies?{" "}
                </legend>
                <div className="mt-2 ml-2 mb-2 image-wrapper" role="group" aria-labelledby="group-level-legend"> {/* Using role="group" with aria-labelledby */}
                  <Field name="groupLevel">
                    {({ field, form: { values, setFieldValue } }) => (
                      <>
                        <WrapFormControlLabel
                          keyText="senior"
                          textLabel="Senior"
                          ControlIcon={SeniorIcon}
                          ActiveControlIcon={SeniorIcon}
                          selected={Array.isArray(values.groupLevel) ? values.groupLevel.includes("senior") : false}
                          handleClick={() => handleImageClick("senior", setFieldValue)}
                          ariaRole={Array.isArray(values.groupLevel) ? "checkbox" : "radio"} // Senior can be multi-selected
                        />
                        <WrapFormControlLabel
                          keyText="military"
                          textLabel="Military"
                          ControlIcon={MilitaryIcon}
                          ActiveControlIcon={MilitaryIcon}
                          selected={Array.isArray(values.groupLevel) ? values.groupLevel.includes("military") : false}
                          handleClick={() => handleImageClick("military", setFieldValue)}
                          ariaRole={Array.isArray(values.groupLevel) ? "checkbox" : "radio"} // Military can be multi-selected
                        />
                        <WrapFormControlLabel
                          keyText="neither"
                          textLabel="Neither"
                          ControlIcon={NeitherIcon}
                          ActiveControlIcon={NeitherIcon}
                          selected={Array.isArray(values.groupLevel) ? values.groupLevel.includes("neither") : false}
                          handleClick={() => handleImageClick("neither", setFieldValue)}
                          ariaRole="radio" // Neither is exclusive
                        />
                      </>
                    )}
                  </Field>
                </div>
              </FormControl>

              <LeafBox style={{
                marginTop: 2,
                marginBottom: 3,
              }}>
                <LeafButton
                  buttonType={"primary"}
                  type="submit"
                  isSubmitting={isSubmitting}>
                  Next
                </LeafButton>
              </LeafBox>
              {v2 ?
                <ProgressBarWithPercentage {...props} />
                :
                <ProgressBar customClass="" {...props} />
              }
              <TermAndCondition />
            </Form>
          </StyledFormContainer>
        </Container>
      )}
    </Formik>
  );
}

export default CustomerGroupStep;
